"use client";
import { DesktopCheckbox } from "@/components/search/search/filters/DesktopCheckbox";
import { useFuzzySearchList } from "@nozbe/microfuzz/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { SecondaryButton } from "ui/components";
import type { PageTags } from "../../_utils/getPageTags";

type Props = {
  onChange: (newValue: string, directUpdate?: boolean) => void;
  onValidate: () => void;
  onClear: () => void;
  localValues: string[];
  selectedValues: string[];
  values: PageTags;
  placeholder: string;
  disableSort?: boolean;
  disableSearch?: boolean;
  large?: boolean;
  category: "Thème" | "Époque" | "Pays";
};
export const MultiSelect = ({
  onChange,
  onValidate,
  onClear,
  localValues,
  selectedValues,
  values,
  placeholder,
  disableSort = false,
  disableSearch = false,
  large,
  category,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const filteredList = useFuzzySearchList({
    list: values,
    queryText: search,
    key: "title",
    mapResultItem: ({ item }) => item,
  });

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node) && isOpen) {
        event.stopPropagation();
        setIsOpen(false);
        setSearch("");
        onValidate();
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen, onValidate]);

  const selectedValuesForCategory = values.filter(
    (v) => v.category === category && selectedValues.includes(v.callbackValue),
  );
  const option = selectedValuesForCategory[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={clsx("h-8 rounded-[28px] border py-1.5 pr-2 text-sm font-medium flex items-center space-x-1", {
          "bg-white hover:bg-[#FBF7F4] border-[#F4EAE1] text-primary stroke-primary":
            selectedValuesForCategory.length === 0,
          "bg-[#FBF7F4] border-pr-1 text-pr-1 stroke-pr-1": selectedValuesForCategory.length !== 0,
          "pl-[0.22rem]": option,
          "pl-3": !option,
        })}
      >
        {option ? (
          <>
            <p
              className="bg-pr-1 flex items-center text-white rounded-[18px] py-0.5 pl-1 pr-1.5"
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.callbackValue, true);
              }}
            >
              <svg
                className="rotate-45"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path d="M8 2V14" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 8L2 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>{option.title}</span>
            </p>
            {selectedValuesForCategory.length > 1 && <span>{`+${selectedValuesForCategory.length - 1}`}</span>}
          </>
        ) : (
          <span> {placeholder}</span>
        )}
        <svg
          className={clsx("transition-transform", { "rotate-180": !isOpen })}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M12 10L8.17678 6.17678C8.07915 6.07915 7.92085 6.07915 7.82322 6.17678L4 10"
            stroke="inherit"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={ref}
          className={clsx(
            "absolute top-full mt-1 left-0 bg-white border border-[#F4EAE1] rounded-md z-10 flex flex-col space-y-2 shadow-xs p-3",
            {
              "w-60": !large,
              "w-96": large,
            },
          )}
        >
          {!disableSearch && (
            <input
              className="p-3 w-full focus:outline-hidden text-sm leading-4 border bg-[#FCF6F0] border-[#F4EAE1] rounded-md focus:bg-white placeholder:text-[#C1B1AC]"
              placeholder="Rechercher"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          )}
          <div
            className={clsx("overflow-y-auto grow max-h-60 grid gap-0.5", {
              "grid-cols-1": !large,
              "grid-cols-2": large,
            })}
          >
            {filteredList
              .sort((a, b) => (disableSort ? 0 : a.title.localeCompare(b.title)))
              .map((value) => (
                <DesktopCheckbox
                  key={value.callbackValue}
                  label={value.title}
                  checked={localValues.some((lt) => lt === value.callbackValue)}
                  onChange={() => onChange(value.callbackValue)}
                />
              ))}
          </div>
          <div
            className={clsx({
              "grid grid-cols-2 gap-1": option,
            })}
          >
            {option && (
              <SecondaryButton
                className="w-full"
                size="sm"
                direction="up"
                onClick={() => {
                  setIsOpen(false);
                  setSearch("");
                  onClear();
                }}
              >
                Effacer
              </SecondaryButton>
            )}
            <SecondaryButton
              className="w-full"
              size="sm"
              direction="up"
              onClick={() => {
                setIsOpen(false);
                setSearch("");
                onValidate();
              }}
            >
              Valider
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
};
